import React, { useEffect, useRef, useState } from 'react';
import { CheckCircleIcon, ClockIcon } from '@heroicons/react/24/outline';
import StepHeader from './StepHeader';
import ConfirmationSummary from './ConfirmationSummary';
import StepNavigation from './StepNavigation';
import { autoplannerAPI } from '../../../services/api';

// Small helper component to keep top navigation state synced
const RegisterNavUpdater = ({ registerNav, onNext, onBack, canNext, canBack }) => {
  const prevRef = useRef(null);
  const onNextRef = useRef(onNext);
  const onBackRef = useRef(onBack);

  // Update refs when props change
  useEffect(() => {
    onNextRef.current = onNext;
  }, [onNext]);

  useEffect(() => {
    onBackRef.current = onBack;
  }, [onBack]);

  useEffect(() => {
    const payload = {
      onNext: onNextRef.current,
      onBack: onBackRef.current,
      canNext,
      canBack,
    };
    const prev = prevRef.current;
    const changed =
      !prev ||
      prev.onNext !== payload.onNext ||
      prev.onBack !== payload.onBack ||
      prev.canNext !== payload.canNext ||
      prev.canBack !== payload.canBack;
    if (changed) {
      registerNav(payload);
      prevRef.current = payload;
    }
  }, [registerNav, canNext, canBack]);
  return null;
};

const Step6Confirmation = ({
  formData,
  trainingGroups,
  processing,
  onComplete,
  onBack,
  taskId,
  registerNav,
}) => {
  // Use refs to store latest values to avoid infinite re-renders
  const onCompleteRef = useRef(onComplete);
  const onBackRef = useRef(onBack);

  // Update refs when props change
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    onBackRef.current = onBack;
  }, [onBack]);

  const [normalizedData, setNormalizedData] = useState(null);
  const [loadingNormalizedData, setLoadingNormalizedData] = useState(false);

  // Load normalized dataset to get excluded rowers per session
  useEffect(() => {
    const loadNormalizedData = async () => {
      if (!taskId) return;

      try {
        setLoadingNormalizedData(true);
        const result = await autoplannerAPI.getNormalizedDataset(taskId);
        if (result.success && result.normalized_data?.success) {
          setNormalizedData(result.normalized_data.normalized_data);
        }
      } catch (error) {
        console.error('Error loading normalized dataset:', error);
      } finally {
        setLoadingNormalizedData(false);
      }
    };

    loadNormalizedData();
  }, [taskId]);

  // Extract excluded rowers per session from normalized data
  const excludedRowersPerSession = normalizedData?.display_data?.excluded_rowers_per_session || {};

  return (
    <div className="space-y-6">
      {registerNav && (
        <RegisterNavUpdater
          registerNav={registerNav}
          onNext={() => onCompleteRef.current()}
          onBack={() => onBackRef.current()}
          canNext={!processing}
          canBack={!processing}
        />
      )}
      <StepHeader
        icon={processing ? ClockIcon : CheckCircleIcon}
        title={processing ? 'Task Processing' : 'Confirm Autoplanner Task'}
        description={
          processing
            ? 'Your autoplanner task is being created. You can review the configuration below.'
            : 'Review your settings before creating the autoplanner task.'
        }
      />
      <div className="px-2 sm:px-0">
        <ConfirmationSummary formData={formData} trainingGroups={trainingGroups} taskId={taskId} />
      </div>

      {/* Excluded Rowers Per Session Summary */}
      {!loadingNormalizedData && Object.keys(excludedRowersPerSession).length > 0 && (
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 sm:p-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <ClockIcon className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Excluded Rowers Per Session</h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p className="mb-3">
                  The following rowers have been excluded from specific sessions through the
                  resolution interface:
                </p>
                <div className="space-y-2">
                  {Object.entries(excludedRowersPerSession).map(([sessionId, exclusions]) => {
                    if (!Array.isArray(exclusions) || exclusions.length === 0) return null;

                    // Get session details from normalized data
                    const sessionDetails = normalizedData?.display_data?.session_details?.find(
                      (session) => session.id.toString() === sessionId
                    );
                    const sessionTitle = sessionDetails?.title || `Session ${sessionId}`;
                    const sessionDate = sessionDetails?.date;

                    return (
                      <div
                        key={sessionId}
                        className="rounded border border-yellow-300 bg-yellow-100 p-2"
                      >
                        <p className="mb-1 text-xs font-medium text-yellow-800">
                          {sessionTitle} {sessionDate && `(${sessionDate})`}:
                        </p>
                        <ul className="space-y-1 text-xs text-yellow-700">
                          {exclusions.map((exclusion, index) => {
                            // Get user details from normalized data
                            const userDetails =
                              normalizedData?.display_data?.user_details?.[exclusion.user_id];
                            const userName = userDetails?.name || `User ${exclusion.user_id}`;

                            return (
                              <li key={index} className="flex items-center justify-between">
                                <span>{userName}</span>
                                {exclusion.reason && (
                                  <span className="text-yellow-600">({exclusion.reason})</span>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {processing ? (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-6">
          <h3 className="mb-3 text-sm font-medium text-blue-800">Task Creation in Progress</h3>
          <div className="space-y-2 text-sm text-blue-700">
            <p>• The autoplanner task is being created and queued for processing</p>
            <p>• The AI will analyze rower availability and create optimal crew assignments</p>
            <p>• You'll receive a notification when the task is complete</p>
            <p>• You can view and modify the results in the autoplanner dashboard</p>
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-6">
          <h3 className="mb-3 text-sm font-medium text-blue-800">What happens next?</h3>
          <div className="space-y-2 text-sm text-blue-700">
            <p>1. The autoplanner task will be created and queued for processing</p>
            <p>2. The AI will analyze rower availability and create optimal crew assignments</p>
            <p>3. You'll receive a notification when the task is complete</p>
            <p>4. You can view and modify the results in the autoplanner dashboard</p>
          </div>
        </div>
      )}

      {processing && (
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-yellow-600"></div>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Creating Task</h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>Please wait while we create your autoplanner task...</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <StepNavigation
        onBack={() => onBackRef.current()}
        onContinue={() => onCompleteRef.current()}
        continueDisabled={processing}
        backDisabled={processing}
        backLabel="Back"
        continueLabel={processing ? 'Creating...' : 'Create Task'}
      />
    </div>
  );
};

export default Step6Confirmation;
